import { useState } from 'react'

const DEFAULT_STAMP = {
  visible: true,
  image: null,
  x: 80,
  y: 20,
  rotation: -8,
  scale: 1,
  aspectRatio: 1,
  formatMode: 'preset', // 'preset' | 'original'
  frameType: 'perforated',
  frameColor: '#ffffff',
  borderWidth: 8,
  cornerRadius: 8,
  shadowEnabled: true,
  shadowColor: '#000000',
  shadowOpacity: 0.45,
  shadowBlur: 12,
  shadowOffsetX: 3,
  shadowOffsetY: 3
}

function StampEditor({ stamps, updateStamps }) {
  const [expanded, setExpanded] = useState({})
  const [sectionOpen, setSectionOpen] = useState(true)

  const addStamp = () => {
    updateStamps([...stamps, { ...DEFAULT_STAMP, x: 50 + Math.random() * 30, y: 20 + Math.random() * 40 }])
  }

  const removeStamp = (index) => {
    updateStamps(stamps.filter((_, i) => i !== index))
  }

  const updateStamp = (index, updates) => {
    const next = stamps.map((s, i) => i === index ? { ...s, ...updates } : s)
    updateStamps(next)
  }

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const stamp = stamps[index]
          const updates = { image: img }
          // If in original mode, update aspect ratio to match new image
          if (stamp.formatMode === 'original') {
            updates.aspectRatio = img.width / img.height
          }
          updateStamp(index, updates)
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }

  const setOriginalFormat = (index) => {
    const stamp = stamps[index]
    if (!stamp.image) return
    updateStamp(index, {
      aspectRatio: stamp.image.width / stamp.image.height,
      formatMode: 'original'
    })
  }

  const setPresetFormat = (index, value) => {
    updateStamp(index, { aspectRatio: value, formatMode: 'preset' })
  }

  const toggleExpand = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))

  return (
    <div className={`menu-section ${!sectionOpen ? 'collapsed' : ''}`}>
      <h3 onClick={() => setSectionOpen(!sectionOpen)}>
        <span className="section-arrow">{sectionOpen ? '▾' : '▸'}</span>
        Stamps ({stamps.length})
      </h3>
      {sectionOpen && (
        <div className="section-body">
          {stamps.map((stamp, i) => (
            <div key={i} className="stamp-item">
              <div className="stamp-item-header" onClick={() => toggleExpand(i)}>
                <span className="section-arrow small">{expanded[i] ? '▾' : '▸'}</span>
                <span className="stamp-item-title">Stamp {i + 1}</span>
                <label className="stamp-toggle" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={stamp.visible}
                    onChange={(e) => updateStamp(i, { visible: e.target.checked })} />
                </label>
                <button className="stamp-remove" onClick={(e) => { e.stopPropagation(); removeStamp(i) }}>×</button>
              </div>

              {expanded[i] && (
                <div className="stamp-item-body">
                  {/* Image */}
                  <div className="control-group">
                    <label className="control-label">Image</label>
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(i, e)} />
                  </div>

                  {/* Position */}
                  <div className="control-row">
                    <div className="control-group half">
                      <label className="control-label">X</label>
                      <input type="range" min="0" max="100" value={stamp.x}
                        onChange={(e) => updateStamp(i, { x: parseFloat(e.target.value) })} />
                      <span className="value-label">{stamp.x.toFixed(0)}%</span>
                    </div>
                    <div className="control-group half">
                      <label className="control-label">Y</label>
                      <input type="range" min="0" max="100" value={stamp.y}
                        onChange={(e) => updateStamp(i, { y: parseFloat(e.target.value) })} />
                      <span className="value-label">{stamp.y.toFixed(0)}%</span>
                    </div>
                  </div>

                  {/* Rotation & Scale */}
                  <div className="control-row">
                    <div className="control-group half">
                      <label className="control-label">Rotation</label>
                      <input type="range" min="-180" max="180" value={stamp.rotation}
                        onChange={(e) => updateStamp(i, { rotation: parseFloat(e.target.value) })} />
                      <span className="value-label">{stamp.rotation.toFixed(0)}°</span>
                    </div>
                    <div className="control-group half">
                      <label className="control-label">Scale</label>
                      <input type="range" min="0.1" max="5" step="0.1" value={stamp.scale}
                        onChange={(e) => updateStamp(i, { scale: parseFloat(e.target.value) })} />
                      <span className="value-label">{stamp.scale.toFixed(1)}×</span>
                    </div>
                  </div>

                  {/* Aspect Ratio / Format */}
                  <div className="control-group">
                    <label className="control-label">Format</label>
                    <div className="format-buttons">
                      {[
                        { label: '1:1', value: 1 },
                        { label: '3:2', value: 1.5 },
                        { label: '2:3', value: 0.67 },
                        { label: '16:9', value: 1.78 },
                      ].map(f => (
                        <button key={f.label}
                          className={`format-btn ${stamp.formatMode === 'preset' && Math.abs(stamp.aspectRatio - f.value) < 0.05 ? 'active' : ''}`}
                          onClick={() => setPresetFormat(i, f.value)}>
                          {f.label}
                        </button>
                      ))}
                      <button
                        className={`format-btn ${stamp.formatMode === 'original' ? 'active' : ''}`}
                        onClick={() => setOriginalFormat(i)}
                        disabled={!stamp.image}
                        title={stamp.image ? `Original (${(stamp.image.width / stamp.image.height).toFixed(2)})` : 'Load an image first'}>
                        Orig
                      </button>
                    </div>
                  </div>

                  {/* Frame Type */}
                  <div className="control-group">
                    <label className="control-label">Frame</label>
                    <select value={stamp.frameType}
                      onChange={(e) => updateStamp(i, { frameType: e.target.value })}>
                      <option value="perforated">Perforated</option>
                      <option value="rounded">Rounded</option>
                      <option value="none">None</option>
                    </select>
                  </div>

                  {stamp.frameType !== 'none' && (
                    <>
                      <div className="control-row">
                        <div className="control-group half">
                          <label className="control-label">Border</label>
                          <input type="range" min="0" max="20" value={stamp.borderWidth}
                            onChange={(e) => updateStamp(i, { borderWidth: parseFloat(e.target.value) })} />
                          <span className="value-label">{stamp.borderWidth}px</span>
                        </div>
                        <div className="control-group half">
                          <label className="control-label">Color</label>
                          <input type="color" value={stamp.frameColor}
                            onChange={(e) => updateStamp(i, { frameColor: e.target.value })} />
                        </div>
                      </div>
                      {stamp.frameType === 'rounded' && (
                        <div className="control-group">
                          <label className="control-label">Corner Radius</label>
                          <input type="range" min="0" max="40" value={stamp.cornerRadius}
                            onChange={(e) => updateStamp(i, { cornerRadius: parseFloat(e.target.value) })} />
                          <span className="value-label">{stamp.cornerRadius}px</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Shadow */}
                  <div className="control-group">
                    <label className="control-label">
                      <input type="checkbox" checked={stamp.shadowEnabled}
                        onChange={(e) => updateStamp(i, { shadowEnabled: e.target.checked })} />
                      Shadow
                    </label>
                  </div>
                  {stamp.shadowEnabled && (
                    <>
                      <div className="control-row">
                        <div className="control-group half">
                          <label className="control-label">Blur</label>
                          <input type="range" min="0" max="40" value={stamp.shadowBlur}
                            onChange={(e) => updateStamp(i, { shadowBlur: parseFloat(e.target.value) })} />
                          <span className="value-label">{stamp.shadowBlur}px</span>
                        </div>
                        <div className="control-group half">
                          <label className="control-label">Opacity</label>
                          <input type="range" min="0" max="1" step="0.05" value={stamp.shadowOpacity ?? 0.45}
                            onChange={(e) => updateStamp(i, { shadowOpacity: parseFloat(e.target.value) })} />
                          <span className="value-label">{((stamp.shadowOpacity ?? 0.45) * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                      <div className="control-row">
                        <div className="control-group half">
                          <label className="control-label">Offset X</label>
                          <input type="range" min="-20" max="20" value={stamp.shadowOffsetX}
                            onChange={(e) => updateStamp(i, { shadowOffsetX: parseFloat(e.target.value) })} />
                          <span className="value-label">{stamp.shadowOffsetX}px</span>
                        </div>
                        <div className="control-group half">
                          <label className="control-label">Offset Y</label>
                          <input type="range" min="-20" max="20" value={stamp.shadowOffsetY}
                            onChange={(e) => updateStamp(i, { shadowOffsetY: parseFloat(e.target.value) })} />
                          <span className="value-label">{stamp.shadowOffsetY}px</span>
                        </div>
                      </div>
                      <div className="control-group">
                        <label className="control-label">Color</label>
                        <input type="color" value={stamp.shadowColor || '#000000'}
                          onChange={(e) => updateStamp(i, { shadowColor: e.target.value })} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          <button onClick={addStamp}>+ Add Stamp</button>
        </div>
      )}
      <div className="section-perf-edge" />
    </div>
  )
}

export default StampEditor
